import { createTheme, alpha, type Theme } from '@mui/material/styles';

export type ColorMode = 'light' | 'dark';

/**
 * MyevCRM — corporate theme factory (light + dark).
 * Clean enterprise palette (deep blue + EV teal accent), Inter typography,
 * soft shadows, rounded surfaces, and polished MUI component defaults.
 */
export function buildTheme(mode: ColorMode): Theme {
  const isDark = mode === 'dark';

  const border = isDark ? '#243049' : '#e6eaf0';
  const surface = isDark ? '#131c2e' : '#ffffff';
  const appBg = isDark ? '#0b1220' : '#f4f6fb';
  const headBg = isDark ? '#18233a' : '#f8fafc';
  const textPrimary = isDark ? '#e2e8f0' : '#0f172a';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const primaryMain = isDark ? '#3b82f6' : '#1e40af';

  return createTheme({
    palette: {
      mode,
      primary: { main: primaryMain, dark: '#1e3a8a', light: '#60a5fa', contrastText: '#ffffff' },
      secondary: { main: '#0d9488', dark: '#0f766e', light: '#2dd4bf', contrastText: '#ffffff' },
      success: { main: '#16a34a', light: isDark ? '#14532d' : '#dcfce7', dark: '#15803d' },
      warning: { main: '#d97706', light: isDark ? '#78350f' : '#fef3c7', dark: '#b45309' },
      error: { main: '#dc2626', light: isDark ? '#7f1d1d' : '#fee2e2', dark: '#b91c1c' },
      info: { main: '#0284c7', light: isDark ? '#0c4a6e' : '#e0f2fe', dark: '#0369a1' },
      background: { default: appBg, paper: surface },
      text: { primary: textPrimary, secondary: textSecondary },
      divider: border,
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 14,
      h4: { fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontWeight: 700, letterSpacing: '-0.01em' },
      h6: { fontWeight: 700, letterSpacing: '-0.01em' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: 'none' },
      overline: { fontWeight: 700, letterSpacing: '0.08em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: appBg },
          '*::-webkit-scrollbar': { width: 8, height: 8 },
          '*::-webkit-scrollbar-thumb': { background: isDark ? '#334155' : '#cbd5e1', borderRadius: 8 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: { backgroundColor: alpha(surface, isDark ? 0.85 : 0.9), backdropFilter: 'blur(8px)', color: textPrimary, borderBottom: `1px solid ${border}` },
        },
      },
      MuiDrawer: { styleOverrides: { paper: { backgroundColor: surface, borderRight: `1px solid ${border}`, backgroundImage: 'none' } } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingInline: 16 },
          containedPrimary: { boxShadow: '0 1px 2px rgba(30,64,175,0.25)' },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 14,
            border: `1px solid ${border}`,
            backgroundImage: 'none',
            boxShadow: isDark ? 'none' : '0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)',
          },
        },
      },
      MuiPaper: { styleOverrides: { rounded: { borderRadius: 14 }, outlined: { borderColor: border }, root: { backgroundImage: 'none' } } },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: headBg,
              color: textSecondary,
              fontWeight: 700,
              fontSize: 11.5,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              borderBottom: `1px solid ${border}`,
              whiteSpace: 'nowrap',
            },
          },
        },
      },
      MuiTableCell: { styleOverrides: { root: { borderColor: border } } },
      MuiTableRow: { styleOverrides: { hover: { '&:hover': { backgroundColor: headBg } } } },
      MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 8 }, sizeSmall: { height: 22 } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 8 }, notchedOutline: { borderColor: border } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 16, backgroundImage: 'none' } } },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700, fontSize: '1.1rem' } } },
      MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: '#0f172a', fontSize: 12, borderRadius: 6 } } },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            marginInline: 8,
            '&.Mui-selected': {
              backgroundColor: alpha(primaryMain, isDark ? 0.22 : 0.1),
              color: primaryMain,
              '&:hover': { backgroundColor: alpha(primaryMain, isDark ? 0.28 : 0.16) },
              '& .MuiListItemIcon-root': { color: primaryMain },
            },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 38, color: textSecondary } } },
    },
  });
}

/** Default light theme (back-compat for any direct importers). */
export const theme = buildTheme('light');
